/**
 * Centralises mutation + confirmation state for AdminJobsPage.
 *
 * Returns handlers (retry/discard/trigger/cancel/pause/resume) along with
 * aggregate `isPending` flags, and hoists confirmation prompts through
 * `useConfirmDialog` so tabs don't duplicate confirm() wiring. Tab views
 * consume this hook directly rather than receiving callbacks as props.
 */
import { useEffect } from 'react'
import { useToast } from '@/components/layout/toastUtils'
import { useConfirmDialog } from '@/components/ui'
import {
  useRetryAdminJob,
  useDiscardAdminJob,
  useTriggerAdminJob,
  useCancelAdminScheduledJob,
  useCancelAllAdminScheduledJobs,
  useCancelAdminPendingJob,
  useCancelAllAdminPendingJobs,
  usePauseAdminQueue,
  useResumeAdminQueue,
} from '@/hooks/admin/useAdmin'

/** Toasts once any of the four cancel mutations succeeds. */
function useCancelledToast(scheduled: boolean, allScheduled: boolean, pending: boolean, allPending: boolean) {
  const { showToast } = useToast()
  useEffect(() => {
    if (scheduled || allScheduled || pending || allPending) {
      showToast('Job(s) cancelled successfully', 'success')
    }
  }, [scheduled, allScheduled, pending, allPending, showToast])
}

export function useAdminJobActions() {
  const retryJob = useRetryAdminJob()
  const discardJob = useDiscardAdminJob()
  const triggerJob = useTriggerAdminJob()
  const cancelScheduledJob = useCancelAdminScheduledJob()
  const cancelAllScheduledJobs = useCancelAllAdminScheduledJobs()
  const cancelPendingJob = useCancelAdminPendingJob()
  const cancelAllPendingJobs = useCancelAllAdminPendingJobs()
  const pauseQueue = usePauseAdminQueue()
  const resumeQueue = useResumeAdminQueue()
  const { dialog: confirmDialog, confirm } = useConfirmDialog()

  useCancelledToast(
    cancelScheduledJob.isSuccess,
    cancelAllScheduledJobs.isSuccess,
    cancelPendingJob.isSuccess,
    cancelAllPendingJobs.isSuccess,
  )

  // Confirm via the dialog, then run `action` only if accepted.
  const confirmThen = async (message: string, options: Parameters<typeof confirm>[1], action: () => void) => {
    if (await confirm(message, options)) action()
  }

  const handleRetry = (id: number) => retryJob.mutate(id)

  const handleDiscard = (id: number) =>
    confirmThen(
      'Discard this failed job? This action cannot be undone.',
      { title: 'Discard Failed Job', confirmLabel: 'Discard' },
      () => discardJob.mutate(id),
    )

  const handleTriggerJob = (jobClass: string) =>
    confirmThen(`Trigger ${jobClass}?`, { title: 'Trigger Job', confirmLabel: 'Trigger', variant: 'primary' }, () =>
      triggerJob.mutate(jobClass),
    )

  const handleCancelScheduled = (id: number) =>
    confirmThen('Cancel this scheduled job?', { title: 'Cancel Scheduled Job', confirmLabel: 'Cancel Job' }, () =>
      cancelScheduledJob.mutate(id),
    )

  const handleCancelAllScheduled = (jobClass?: string) =>
    confirmThen(
      jobClass ? `Cancel all scheduled ${jobClass} jobs?` : 'Cancel ALL scheduled jobs?',
      { title: 'Cancel Scheduled Jobs', confirmLabel: 'Cancel Jobs' },
      () => cancelAllScheduledJobs.mutate(jobClass),
    )

  const handleCancelPending = (id: number) =>
    confirmThen('Cancel this pending job?', { title: 'Cancel Pending Job', confirmLabel: 'Cancel Job' }, () =>
      cancelPendingJob.mutate(id),
    )

  const handleCancelAllPending = (jobClass: string) =>
    confirmThen(
      `Cancel all pending ${jobClass} jobs?`,
      { title: 'Cancel Pending Jobs', confirmLabel: 'Cancel Jobs' },
      () => cancelAllPendingJobs.mutate(jobClass),
    )

  return {
    confirmDialog,
    retry: { mutate: handleRetry, isPending: retryJob.isPending },
    discard: { mutate: handleDiscard, isPending: discardJob.isPending },
    trigger: {
      mutate: handleTriggerJob,
      isPending: triggerJob.isPending,
      isSuccess: triggerJob.isSuccess,
      isError: triggerJob.isError,
      error: triggerJob.error,
    },
    cancelScheduled: { mutate: handleCancelScheduled, isPending: cancelScheduledJob.isPending },
    cancelAllScheduled: { mutate: handleCancelAllScheduled, isPending: cancelAllScheduledJobs.isPending },
    cancelPending: { mutate: handleCancelPending, isPending: cancelPendingJob.isPending },
    cancelAllPending: { mutate: handleCancelAllPending, isPending: cancelAllPendingJobs.isPending },
    pauseQueue,
    resumeQueue,
  }
}

export type AdminJobActions = ReturnType<typeof useAdminJobActions>
